// @ts-nocheck
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import { StyledButton } from "@insite/mobius/Clickable/Clickable";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formSmallGridProps } from "@store/Themes/common/props/form";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { SelectPresentationProps } from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import { ButtonIconPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { infoProps } from "@store/Themes/common/props/info";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
/* Store Custom Code - End */

export const tableGetEllipsisCellProps = (width: number): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle}, > ${TypographyStyle} > ${StyledButton} {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
            max-width: ${pxToRem(width)};
        }

        > ${StyledButton} {
            display: inline-block;
        }
    `,
});

export const tableRemoveEllipsisCellProps = (): DataTableCellProps => ({
    css: css`
        > ${StyledButton}, > ${TypographyStyle} {
            overflow: visible;
            white-space: inherit;
            max-width: none;
        }
    `,
});

/* Store Custom Code - Start */
export const mergeCssToProps = (props: any, _css: InjectableCss) => {
    return {
        ...props,
        css: css`
            ${props.css}
            ${_css.css}
        `,
    };
};
export const hideUnder = (bp: string) => {
    return {
        css: css`
            ${breakpoint(bp, "down")} {
                display: none;
            }
        `,
    };
};
/* Store Custom Code - End */

export interface TableProps {
    loadingOverlay?: LoadingOverlayProps;
    headingWrapper?: FlexWrapperPresentationProps;
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    selectionHeader?: DataTableHeaderProps;
    selectionCell?: DataTableCellProps;
    amountHeader?: DataTableHeaderProps;
    amountCell?: DataTableCellProps;
    tightHeader?: DataTableHeaderProps;
    tightCell?: DataTableCellProps;
    button?: ButtonPresentationProps;
    narrowCell?: DataTableCellProps;
    // Store Custom Code
    responsiveTableHeader?: DataTableHeaderProps;
    responsiveTableCells?: DataTableCellProps;
    infoRow?: InjectableCss;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    sortWrapper?: InjectableCss;
    sortSelect?: SelectPresentationProps;
    sortButton?: ButtonIconPresentationProps;
}

const selectionStyles = (invert?: boolean) => css`
    text-align: center;
    line-height: 1;

    ${CheckboxStyle} {
        display: flex;

        [role="checkbox"] {
            margin: 0;
            ${invert ? `border-color: transparent !important;` : null}

            + label {
                padding: 0;
            }
        }
    }

    ${RadioStyle} {
        display: flex;

        label {
            font-size: 0;
            color: transparent;

            &::before,
            &::after {
                margin: 0;
            }
        }
    }
`;

export const tableProps: TableProps = {
    loadingOverlay: { minHeight: 150 },
    headingWrapper: {
        gap: pxToRem(16),
        verticalGap: pxToRem(8),
        extendedStyles: {
            wrapper: resultCountProps.wrapper,
            content: {
                css: css`
                    justify-content: space-between;
                `,
            },
        },
    },
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            overflow-x: auto;
        `,
    },
    selectionHeader: {
        tight: true,
        css: selectionStyles(true),
    },
    selectionCell: {
        tight: true,
        css: selectionStyles(),
    },
    amountHeader: {
        tight: true,
        css: css`
            text-align: right;

            > ${StyledButton} {
                justify-content: flex-end;
            }
        `,
    },
    amountCell: {
        tight: true,
        css: css`
            text-align: right;
        `,
    },
    tightHeader: { tight: true },
    tightCell: {
        tight: true,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    button: {
        color: "primary",
        sizeVariant: "small",
        css: css`
            ${x => x.theme.button.primary.css}
            position: relative;
            min-width: 0;
        `,
    },
    narrowCell: {
        css: css`
            padding-top: ${pxToRem(8)};
            padding-bottom: ${pxToRem(8)};
        `,
    },
    // Store Custom Code
    responsiveTableHeader: {
        css: css`
            padding: ${pxToRem(12, 15)};
            ${breakpoint("xl", "up")} {
                display: none;
            }
        `,
    },
    responsiveTableCells: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;

            ${TypographyStyle} {
                word-break: break-word;
            }
            ${breakpoint("xl", "up")} {
                display: none;
            }
        `,
    },
    infoRow: {
        css: css`
            display: flex;
            flex-direction: row;
            white-space: normal;
            margin: ${pxToRem(2)} 0;
        `,
    },
    infoLabel: mergeCssToProps(infoProps.label, {
        css: css`
            white-space: nowrap;
        `,
    }),
    infoValue: infoProps.value,
    sortWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flext-start;

            > div {
                ${inlineSeparatorStyles({ spacing: "2em", spacingType: "padding", side: "left", height: "20px" })}
            }
        `,
    },
    sortSelect: {
        cssOverrides: {
            formField: css`
                width: auto;
            `,
            inputSelect: css`
                color: ${getColor("tertiary.main")};

                &&:not(.selected)[data-selected-index=""] {
                    color: ${getColor("tertiary.main")};

                    &:disabled,
                    &.disabled {
                        color: ${getColor("placeholder.main")};
                    }
                }

                option {
                    color: ${getColor("text.input")};

                    select:not(.selected) &[value=""] {
                        color: ${getColor("tertiary.main")};
                    }

                    &:disabled {
                        background-color: ${x => getColor("common.disabled")};
                        color: ${getColor("text.disabled")};
                    }
                }
            `,
        },
        labelProps: {
            css: css`
                color: ${getColor("tertiary.contrast")};
            `,
        },
        iconProps: {
            color: "tertiary.contrast",
        },
    },
    sortButton: {
        size: 24,
        color: "tertiary.contrast",
        css: css`
            margin-left: ${pxToRem(10)};
            border: ${pxToRem(2)} solid ${getColor("tertiary.contrast")};
            border-radius: ${pxToRem(24)};

            &:hover {
                transform: rotate(180deg);
                cursor: pointer;
            }
        `,
    },
};

export interface TableFilterProps {
    wrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    formGridContainer?: GridContainerProps;
    clearButton?: ButtonPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export const tableFilterProps: TableFilterProps = {
    wrapper: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)}
        `,
    },
    accordion: {
        mergeCss: true,
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.base)}
        `,
    },
    formGridContainer: formSmallGridProps.gridContainer,
    clearButton: { variant: "secondary" },
};
